import React, { useState } from 'react';
import { Navigate, useNavigate, useOutletContext } from 'react-router';
import { SEOHelmet } from 'components/SEO';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { CountStepper } from '../../components/CountStepper';
import { useReportResultMutation } from 'store/services/play/playApi';
import { playErrorMessage } from 'utils/play/playErrors';
import { playPath } from 'utils/play/inviteUrl';
import PlayNotFound from '../../PlayNotFound';
import { PlayLayout } from '../../layout/PlayLayout';
import type { TournamentOutletContext } from '../../PlayRoutes';
import { useTournament } from '../../hooks/useTournament';
import { canDecrementGames, canIncrementGames, isEliminationRound, isReportableResult, myMatchView, type MyMatchView } from '../../matchView';
import { TournamentLoading } from '../Tournament/TournamentHomePage';
import './PlayerHome.css';

/** Summary copy driven only by (mine, opp, elimination) (Asana 1220065638706450, Copy block). */
export const reportSummary = (
  mine: number,
  opp: number,
  elimination: boolean,
  names: { me: string; opp: string },
): { headline: string; helper: string; muted: boolean } => {
  const notified = `${names.opp} gets notified. The result is final unless they dispute it.`;
  if (mine === 0 && opp === 0) return { headline: 'Set the score', helper: 'Tap + for each game a player won.', muted: false };
  if (isReportableResult(mine, opp, elimination)) {
    if (mine > opp) return { headline: `${names.me} wins ${mine}–${opp}`, helper: notified, muted: false };
    if (opp > mine) return { headline: `${names.opp} wins ${opp}–${mine}`, helper: notified, muted: false };
    return { headline: `Draw ${mine}–${opp}`, helper: notified, muted: false };
  }
  if (mine === opp && elimination) return { headline: `${mine}–${opp}`, helper: "Elimination matches can't end in a draw.", muted: true };
  return { headline: `${mine}–${opp}`, helper: 'Best of 3: a match ends 2–0, 2–1 or 1–1.', muted: true };
};

interface FormProps {
  code: string;
  my: MyMatchView;
  elimination: boolean;
  tournamentName: string;
  roundNumber: number | undefined;
}

/**
 * Decisions 8 and 14: either player submits; the result is final unless the
 * opponent reports something different. One stepper row per player (Asana
 * 1220065638706450): no mine-first option list to decode. Players can change
 * their own report until the next round is paired; reopening pre-fills it.
 */
const ReportForm: React.FC<FormProps> = ({ code, my, elimination, tournamentName, roundNumber }) => {
  const navigate = useNavigate();
  const [report, { isLoading: saving }] = useReportResultMutation();
  const [mine, setMine] = useState<number>(my.games ? my.games[0] : 0);
  const [opp, setOpp] = useState<number>(my.games ? my.games[1] : 0);
  const [error, setError] = useState<string | null>(null);

  const meName = my.me.username;
  const oppName = my.opponent?.username ?? 'Opponent';
  const reportable = isReportableResult(mine, opp, elimination);
  const summary = reportSummary(mine, opp, elimination, { me: meName, opp: oppName });

  const submit = async () => {
    if (!reportable || saving) return;
    setError(null);
    try {
      await report({ matchId: my.match.id, code, gamesMine: mine, gamesOpp: opp }).unwrap();
      navigate(playPath(code), { replace: true });
    } catch (err) {
      setError(playErrorMessage(err, "Couldn't save the result. Try again."));
    }
  };

  return (
    <PlayLayout title={tournamentName} subtitle={`Round ${roundNumber} · Table ${my.tableNumber ?? '?'}`} backTo={playPath(code)}>
      <SEOHelmet title="Report result - TradeMagic Play" description="Report your match result." noindex />
      <section className="play-card" data-testid="player-report">
        <p className="play-eyebrow">Report result</p>
        <h1 className="play-title">Games won</h1>
        {my.disputed && (
          <div className="play-result-banner play-result-banner--disputed" style={{ marginTop: 12 }}>
            <p className="play-result-banner__title">This match is disputed</p>
            <p className="play-result-banner__sub">You and your opponent reported different results. The organizer will settle it; you can still update your report.</p>
          </div>
        )}
        <div className="play-report__steppers">
          <CountStepper
            size="lg"
            label={meName}
            hint="You"
            display={String(mine)}
            onDecrement={() => setMine((n) => Math.max(0, n - 1))}
            onIncrement={() => setMine((n) => (canIncrementGames(n, opp) ? n + 1 : n))}
            canDecrement={canDecrementGames(mine)}
            canIncrement={canIncrementGames(mine, opp)}
            winner={mine > opp}
            testId="report-games-mine"
            ariaLabel={`Games won by ${meName}`}
          />
          <CountStepper
            size="lg"
            label={oppName}
            hint="Opponent"
            display={String(opp)}
            onDecrement={() => setOpp((n) => Math.max(0, n - 1))}
            onIncrement={() => setOpp((n) => (canIncrementGames(n, mine) ? n + 1 : n))}
            canDecrement={canDecrementGames(opp)}
            canIncrement={canIncrementGames(opp, mine)}
            winner={opp > mine}
            testId="report-games-opp"
            ariaLabel={`Games won by ${oppName}`}
          />
        </div>
        <div className="play-report__summary" data-testid="report-summary" aria-live="polite">
          <p className={`play-report__headline${summary.muted ? ' play-report__headline--muted' : ''}`}>{summary.headline}</p>
          <p className="play-report__helper">{summary.helper}</p>
        </div>
        <div className="play-report__cta">
          <PlayPrimaryButton label="Submit result" disabled={!reportable || saving} loading={saving} onClick={submit} />
        </div>
        {error && (
          <p className="play-error" role="alert">
            {error}
          </p>
        )}
      </section>
    </PlayLayout>
  );
};

const ReportPage: React.FC = () => {
  const { code } = useOutletContext<TournamentOutletContext>();
  const { state, isLoading, notFound } = useTournament(code);

  if (notFound) return <PlayNotFound reason="missing" />;
  if (isLoading || !state) return <TournamentLoading />;
  if (state.viewer.role !== 'PLAYER') return <Navigate replace to={playPath(code)} />;

  const my = myMatchView(state.myMatch, state.viewer);
  const live = state.tournament.status === 'LIVE' && !!state.currentRound?.startedAt;
  if (!my || my.isBye || !live) return <Navigate replace to={playPath(code)} />;

  const elimination = isEliminationRound(state, state.currentRound);
  // Keyed on the match so a re-pair remounts the form with fresh initial values.
  return <ReportForm key={my.match.id} code={code} my={my} elimination={elimination} tournamentName={state.tournament.name} roundNumber={state.currentRound?.number} />;
};

export default ReportPage;
